import { useEffect, useState } from "react";
import api, { windowLabel, WINDOWS } from "@/lib/api";
import { toast } from "sonner";
import { Pause, Play, X as XIcon, Save, Sparkles, CreditCard } from "lucide-react";

export default function PlanPage() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState({});
  const [portalLoading, setPortalLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/subscriptions/my");
    setSubs(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/billing-portal", { return_url: window.location.href });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Billing portal unavailable.");
      setPortalLoading(false);
    }
  };

  const upd = async (id, payload) => {
    try {
      await api.put(`/subscriptions/${id}`, payload);
      toast.success("Updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const cancelWithRetention = (id) => {
    if (!window.confirm("Are you sure? Your trash will miss us. 🥺\n\nWe'll pause for one week instead — sound okay?")) return;
    if (window.confirm("Still want to cancel completely?")) {
      upd(id, { status: "canceled" });
    } else {
      upd(id, { status: "paused" });
    }
  };

  const saveEdits = (id) => {
    const e = edit[id];
    if (!e) return;
    const payload = {};
    if (e.bags !== undefined) payload.bags = e.bags;
    if (e.windows !== undefined) payload.windows = e.windows;
    if (e.monthly_cleaning !== undefined) payload.monthly_cleaning = e.monthly_cleaning;
    upd(id, payload);
    setEdit((prev) => ({ ...prev, [id]: undefined }));
  };

  if (loading) return <div className="p-12 text-center text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-testid="plan-title">
        <Sparkles strokeWidth={2.5}/> My Plan
      </h1>
      <p className="text-slate-600 mb-4">Manage your weekly recurring service.</p>

      {subs.some((s) => s.status === "active") && (
        <div className="card-clay p-4 mb-4 bg-blue-50 border-blue-200 flex items-center justify-between flex-wrap gap-3" data-testid="billing-portal-card">
          <div>
            <div className="font-fredoka font-bold text-slate-900 flex items-center gap-2"><CreditCard size={18} strokeWidth={2.5}/> Update card or cancel via Stripe</div>
            <div className="text-xs text-slate-600 mt-0.5">Securely update your payment method or fully cancel future weekly charges from your Stripe customer portal.</div>
          </div>
          <button onClick={openBillingPortal} disabled={portalLoading} className="btn-push-outline px-4 py-2 text-sm whitespace-nowrap disabled:opacity-60" data-testid="open-billing-portal">
            {portalLoading ? "Opening…" : "Manage billing"}
          </button>
        </div>
      )}

      {subs.length === 0 ? (
        <div className="card-clay p-6 text-center">
          <div className="font-fredoka font-bold text-lg">No recurring service yet</div>
          <p className="text-slate-500 text-sm mt-1">Set one up from the Schedule page.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="plan-subs">
          {subs.map((s) => {
            const e = edit[s.id] || {};
            const bags = e.bags ?? s.bags;
            const windows = e.windows ?? s.windows;
            const cleaning = e.monthly_cleaning ?? s.monthly_cleaning;
            const dirty = e.bags !== undefined || e.windows !== undefined || e.monthly_cleaning !== undefined;
            return (
              <div key={s.id} className="card-clay p-5" data-testid={`plan-${s.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-fredoka font-bold text-lg">{s.community} · Bldg {s.building}</div>
                    <div className="text-xs text-slate-500">Unit {s.unit}</div>
                  </div>
                  <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-full ${s.status === "active" ? "bg-green-100 text-green-800" : s.status === "paused" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-700"}`}>{s.status}</span>
                </div>

                <div className="mt-4">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Pickup windows</div>
                  <div className="flex flex-wrap gap-2">
                    {WINDOWS.map((w) => {
                      const on = windows.includes(w.value);
                      return (
                        <button key={w.value} onClick={() => setEdit((p) => ({ ...p, [s.id]: { ...e, windows: on ? windows.filter(x => x !== w.value) : [...windows, w.value] } }))} className={`rounded-xl px-3 py-1.5 border-2 text-sm font-semibold ${on ? "bg-green-500 text-white border-green-700" : "bg-white border-slate-200 text-slate-700"}`} data-testid={`plan-win-${s.id}-${w.value}`}>
                          {w.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Bags per pickup</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEdit((p) => ({ ...p, [s.id]: { ...e, bags: Math.max(1, bags - 1) } }))} className="w-9 h-9 rounded-xl bg-slate-100 border-2 border-slate-200" data-testid={`plan-bags-dec-${s.id}`}>−</button>
                      <div className="font-fredoka text-2xl font-bold w-12 text-center">{bags}</div>
                      <button onClick={() => setEdit((p) => ({ ...p, [s.id]: { ...e, bags: Math.min(20, bags + 1) } }))} className="w-9 h-9 rounded-xl bg-slate-100 border-2 border-slate-200" data-testid={`plan-bags-inc-${s.id}`}>+</button>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 rounded-2xl border-2 border-slate-200 p-3 cursor-pointer">
                    <input type="checkbox" checked={!!cleaning} onChange={(e2) => setEdit((p) => ({ ...p, [s.id]: { ...e, monthly_cleaning: e2.target.checked } }))} className="w-5 h-5 accent-amber-500" data-testid={`plan-cleaning-${s.id}`}/>
                    <span className="text-sm font-semibold text-slate-700">Monthly bin cleaning add-on</span>
                  </label>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {dirty && (
                    <button onClick={() => saveEdits(s.id)} className="btn-push-green px-4 py-2 text-sm inline-flex items-center gap-1" data-testid={`plan-save-${s.id}`}><Save size={14}/> Save changes</button>
                  )}
                  {s.status === "active" && (
                    <button onClick={() => upd(s.id, { status: "paused" })} className="btn-push-outline px-4 py-2 text-sm inline-flex items-center gap-1" data-testid={`plan-pause-${s.id}`}><Pause size={14}/> Pause</button>
                  )}
                  {s.status === "paused" && (
                    <button onClick={() => upd(s.id, { status: "active" })} className="btn-push-green px-4 py-2 text-sm inline-flex items-center gap-1" data-testid={`plan-resume-${s.id}`}><Play size={14}/> Resume</button>
                  )}
                  {s.status !== "canceled" && (
                    <button onClick={() => cancelWithRetention(s.id)} className="btn-push-outline px-4 py-2 text-sm inline-flex items-center gap-1 text-red-600" data-testid={`plan-cancel-${s.id}`}><XIcon size={14}/> Cancel</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
